<template>
    <app-error v-if="groupEventData === null" />
    <template v-else>
        <div>
            <div class="text-3xl">
                {{ groupEventData.title }}
            </div>
            <div class="text-2xl">
                {{ subtitle }}
            </div>
            <div>Please fill out all the required documents below</div>
        </div>

        <content-reset
            v-if="groupEventData.markup !== ''"
            :body="groupEventData.markup"
        />

        <div class="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <template
                v-for="plan in requiredDocuments"
                :key="plan.id"
            >
                <span v-if="plan.presentation === null">
                    Oops! We could not find the presentation for this document.
                    Please contact the facility to resolve.
                </span>

                <nuxt-link
                    v-else
                    :to="
                        facilityCreateContractLink(
                            `/${route.params.facility}`,
                            plan.planType,
                            plan.slug
                        )
                    "
                >
                    <span
                        class="group flex h-full flex-col border border-gray-200 bg-white hover:border-primary-500 dark:border-gray-700 dark:bg-gray-900"
                    >
                        <span
                            class="flex items-center justify-center bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"
                        >
                            <span
                                class="relative block max-h-[16rem] min-h-[16rem] w-full p-4"
                            >
                                <img
                                    v-if="
                                        plan.presentation.bookingImage !== null
                                    "
                                    :src="plan.presentation.bookingImage"
                                    :alt="
                                        getPlanTitle(
                                            plan.presentation.title,
                                            plan.name
                                        )
                                    "
                                    class="m-auto max-h-[14rem] shadow-lg"
                                />
                            </span>
                        </span>
                        <span class="flex flex-1 flex-col p-4">
                            <span
                                class="text-xl font-medium text-gray-900 dark:text-gray-100"
                            >
                                {{
                                    getPlanTitle(
                                        plan.presentation.title,
                                        plan.name
                                    )
                                }}
                            </span>
                            <span
                                class="line-height-3 mt-2 flex-1 text-gray-700 dark:text-gray-400"
                            >
                                <span
                                    class="ContentReset"
                                    v-html="plan.presentation.shortSummary"
                                />
                            </span>
                            <span class="mt-2 flex justify-end">
                                <prime-button class="p-1">
                                    Fill Out Now
                                </prime-button>
                            </span>
                        </span>
                    </span>
                </nuxt-link>
            </template>
        </div>
    </template>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const GroupManagementStorefrontPlanContractFragment =
    graphql(/* GraphQL */ `
        fragment GroupManagementStorefrontPlanContractFragment on GroupEvent {
            title
            start
            end
            markup
            requiredDocuments {
                id
                name
                presentation(input: $input, language: ENGLISH) {
                    title
                    bookingImage
                    shortSummary
                }
                slug
                planType
            }
        }
    `)
</script>

<script setup lang="ts">
import ContentReset from '~/components/ContentReset.vue'
import AppError from '~/components/NotFound/AppError.vue'
import { type FragmentType, getFragmentData } from '~/resources/graphql'
import { useRouteLink } from '~/composables/routing'
import { computed } from 'vue'
import NuxtLink from '#app/components/nuxt-link'
import PrimeButton from 'primevue/button'
import { useRoute } from '#imports'
import { formatRange, parseLocal } from '~/composables/support/localization'

const props = defineProps<{
    parentGroupBooking: FragmentType<
        typeof GroupManagementStorefrontPlanContractFragment
    >
}>()

const groupEventData = computed(() =>
    getFragmentData(
        GroupManagementStorefrontPlanContractFragment,
        props.parentGroupBooking
    )
)

const subtitle = computed(() => {
    return formatRange(
        parseLocal(groupEventData.value.start),
        parseLocal(groupEventData.value.end)
    )
})

const route = useRoute()
const { facilityCreateContractLink } = useRouteLink()
const requiredDocuments = computed(() => {
    if (groupEventData.value === undefined) {
        return []
    }

    return groupEventData.value.requiredDocuments.map(plan => {
        if (plan.planType !== 'AGREEMENT') {
            return null
        }

        return plan
    })
})

function getPlanTitle(presentationPublicTitle: string, planName: string) {
    if (presentationPublicTitle === '') {
        return planName
    }

    return presentationPublicTitle
}
</script>
